import React, { useCallback, useMemo, useState } from "react";

import {
  MenuItem,
  SplitButton,
  Button,
  mergeClasses,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Body1,
  ProgressBar,
  Image,
  PopoverTrigger,
  PopoverSurface,
  Popover
} from "@fluentui/react-components";
import {
  MicFilled,
  Speaker220Filled,
  Settings20Filled,
  ChevronUpFilled,
  MicOffFilled,
  Dismiss24Regular,
  InfoRegular
} from "@fluentui/react-icons";
import configManager from "../../singleton/ConfigManager";
import { useMicrophoneStatus } from "../room/useMicrophoneStatus";
import { useCan } from "../room/useCan";
import { useMicrophone } from "../room/useMicrophone";
import { PermissionStatus } from "../../utils/media-devices-utils";
import PermissionDenied from "../../assets/images/PermissionDenied.png";
import { MicPopoverSelect } from "../fluent/MicPopoverSelect";
import { useVolumeMeter } from "../misc/useVolumeMeter";
import { useSpeakers } from "../room/useSpeakers";
import { useSound } from "../room/useSound";
import { SOUND_SPEAKER_TONE } from "../../systems/sound-effects-system";
import { useSettingsContext } from "../room/SettingsContext";

export default function ToolbarMic({ styles, scene }) {
  const { micDeviceChanged, micDevices } = useMicrophone(scene);
  const { speakerDeviceChanged, speakerDevices } = useSpeakers();

  const { isMicMuted, toggleMute, permissionStatus, isMicEnabled } = useMicrophoneStatus(scene);
  const micPermissionDenied = permissionStatus === PermissionStatus.DENIED;
  const canVoiceChat = useCan("voice_chat") && configManager.config.voiceChat.enabled;
  const microphoneIcon = useMemo(
    () =>
      isMicMuted || !canVoiceChat || micPermissionDenied || !isMicEnabled ? (
        <MicOffFilled
          style={{
            width: "24px",
            height: "24px",
            color: micPermissionDenied || !canVoiceChat || isMicMuted ? "#EB5757" : "inherit"
          }}
        />
      ) : (
        <MicFilled style={{ width: "24px", height: "24px" }} />
      ),
    [isMicMuted, canVoiceChat, micPermissionDenied, isMicEnabled]
  );

  const onClick = () => toggleMute();

  const primaryActionButtonProps = {
    onClick
  };

  const micButtonDisabled = useMemo(() => !canVoiceChat || micPermissionDenied, [canVoiceChat, micPermissionDenied]);

  const [micChanges, setMicChanges] = useState(0);

  const updateMic = useCallback(level => {
    setMicChanges(level);
  }, []);

  useVolumeMeter({
    analyser: scene.systems["hubs-systems"].audioSystem.outboundAnalyser,
    update: updateMic
  });

  const { playSound } = useSound({
    scene,
    sound: SOUND_SPEAKER_TONE
  });

  const { onOpenChange: onOpenSettingsChange, onTabChange: onTabSettingsChange } = useSettingsContext();

  const onOpenSettings = () => {
    onOpenSettingsChange(true);
    onTabSettingsChange("audiovideo");
  };

  if (micPermissionDenied) {
    return (
      <Dialog>
        <DialogTrigger disableButtonEnhancement>
          {triggerProps => (
            <SplitButton
              primaryActionButton={primaryActionButtonProps}
              menuButton={triggerProps}
              appearance="subtle"
              className={
                micButtonDisabled ? mergeClasses(styles.buttonSplit, styles.buttonSplitDisabled) : styles.buttonSplit
              }
              icon={microphoneIcon}
              menuIcon={<InfoRegular style={{ width: "16px", height: "16px", marginBottom: "4px", color: "#fff" }} />}
            />
          )}
        </DialogTrigger>
        <DialogSurface style={{ maxWidth: "480px" }}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger>
                  <Button appearance="transparent" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              The metaverse is blocked from using your microphone
            </DialogTitle>
            <DialogContent style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <Body1 style={{ color: "#bdbdbd" }}>1.Click the page info icon on your browser's address bar.</Body1>
                <Body1 style={{ color: "#bdbdbd" }}>2.Turn on the microphone permissions.</Body1>
              </div>
              <Image src={PermissionDenied} style={{ borderRadius: "8px" }} />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  }

  return (
    <Popover positioning="below">
      <PopoverTrigger disableButtonEnhancement>
        {triggerProps => (
          <SplitButton
            menuButton={triggerProps}
            disabled={micButtonDisabled}
            primaryActionButton={primaryActionButtonProps}
            appearance="subtle"
            className={
              micButtonDisabled ? mergeClasses(styles.buttonSplit, styles.buttonSplitDisabled) : styles.buttonSplit
            }
            icon={microphoneIcon}
            menuIcon={<ChevronUpFilled style={{ width: "16px", height: "16px", marginBottom: "4px", color: "#fff" }} />}
          />
        )}
      </PopoverTrigger>
      <PopoverSurface
        style={{
          width: "450px",
          maxWidth: "100%",
          display: "flex",
          gap: "10px",
          alignItems: "center",
          padding: "8px 4px"
        }}
        className={styles.menuPopover}
      >
        <MicPopoverSelect positioning="above" name={"mic"} Icon={MicFilled} {...micDevices} onChange={micDeviceChanged}>
          <MenuItem className={styles.micProgressContainer} icon={<MicFilled />}>
            <ProgressBar className={styles.progressBarColor} value={micChanges} thickness="large" color="#219653" />
          </MenuItem>
        </MicPopoverSelect>

        <MicPopoverSelect
          positioning="above"
          name={"speaker"}
          Icon={Speaker220Filled}
          {...speakerDevices}
          onChange={speakerDeviceChanged}
        >
          <MenuItem
            persistOnClick={true}
            className={styles.menuListItem}
            onClick={playSound}
            icon={<Speaker220Filled />}
          >
            Test Speaker
          </MenuItem>
        </MicPopoverSelect>

        <Button onClick={onOpenSettings} appearance="transparent" icon={<Settings20Filled />} />
      </PopoverSurface>
    </Popover>
  );
}

const MicSplitButton = React.forwardRef((props, ref) => {
  return <SplitButton {...props} ref={ref} />;
});
